import { useState } from 'react';
import { CalendarEvent, localTagConfig, LocalEventTag, memberPerkConfig } from '../../../mocks/eventsCalendar';

interface LocalEventsSectionProps {
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
  onSubmitEvent: () => void;
}

export default function LocalEventsSection({ events, onEventClick, onSubmitEvent }: LocalEventsSectionProps) {
  const [activeTag, setActiveTag] = useState<LocalEventTag | 'all'>('all');

  const tagFilters: Array<{ key: LocalEventTag | 'all'; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'featured-partner', label: 'Featured Partner' },
    { key: 'mama-approved', label: 'Mama Approved' },
    { key: 'community-share', label: 'Community Share' },
  ];

  const filtered = activeTag === 'all'
    ? events
    : events.filter((e) => e.localTag === activeTag);

  return (
    <section id="local-events" className="py-14 bg-gray-50/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-600 text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
              <i className="ri-map-pin-2-line text-slate-500 text-xs"></i>
              Around Gwinnett &amp; Surrounding Areas
            </div>
            <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Events Around Town
            </h2>
            <p className="text-sm text-gray-500 mt-1">Family-friendly events across Gwinnett and surrounding areas</p>
          </div>
          <button
            onClick={onSubmitEvent}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 text-xs font-semibold rounded-full hover:border-pink-300 hover:text-pink-600 transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            <i className="ri-add-circle-line"></i>
            Submit an Event
          </button>
        </div>

        {/* Tag filter + legend */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {tagFilters.map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveTag(f.key)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                activeTag === f.key
                  ? 'bg-gray-800 text-white border-gray-800'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {f.key !== 'all' && (
                <i className={`${localTagConfig[f.key as LocalEventTag].icon} text-xs`}></i>
              )}
              {f.label}
            </button>
          ))}

          {/* Legend */}
          <div className="ml-auto hidden sm:flex items-center gap-2 flex-wrap justify-end">
            {(Object.keys(localTagConfig) as LocalEventTag[]).map((key) => {
              const cfg = localTagConfig[key];
              return (
                <span key={key} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                  <i className={`${cfg.icon} text-[10px]`}></i>
                  {cfg.label}
                </span>
              );
            })}
          </div>
        </div>

        {/* Events Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
              <i className="ri-calendar-close-line text-gray-400 text-2xl"></i>
            </div>
            <p className="text-sm font-semibold text-gray-600">No events found</p>
            <p className="text-xs text-gray-400 mt-1">Try a different filter or check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((ev) => {
              const dateObj = new Date(ev.date + 'T00:00:00');
              const month = dateObj.toLocaleString('en-US', { month: 'short' }).toUpperCase();
              const day = dateObj.getDate();
              const weekday = dateObj.toLocaleString('en-US', { weekday: 'short' });
              const tagKey = ev.localTag ?? 'community-share';
              const tagCfg = localTagConfig[tagKey];

              return (
                <div
                  key={ev.id}
                  onClick={() => onEventClick(ev)}
                  className="bg-white rounded-2xl overflow-hidden cursor-pointer group border border-gray-100 hover:border-gray-200 transition-all"
                >
                  {/* Image */}
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={ev.image}
                      alt={ev.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>

                    {/* Date badge */}
                    <div className="absolute top-3 left-3 bg-white rounded-lg px-2 py-1 text-center shadow-sm min-w-[40px]">
                      <div className="text-[9px] font-bold text-pink-500 leading-none">{month}</div>
                      <div className="text-sm font-black text-gray-900 leading-tight">{day}</div>
                      <div className="text-[9px] text-gray-400 leading-none">{weekday}</div>
                    </div>

                    {/* Tag badge */}
                    <div className={`absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full ${tagCfg.bg} ${tagCfg.color}`}>
                      <i className={`${tagCfg.icon} text-[9px]`}></i>
                      {tagCfg.label}
                    </div>

                    {/* Member Perk badge */}
                    {ev.memberPerk && (
                      <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-rose-600 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide">
                        <i className="ri-vip-crown-fill text-[9px]"></i>
                        Member Perk
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="text-sm font-bold text-gray-900 line-clamp-2 leading-snug mb-2 group-hover:text-pink-600 transition-colors">
                      {ev.title}
                    </h3>

                    <div className="space-y-1 mb-3">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <i className="ri-time-line text-gray-400 text-xs"></i>
                        {ev.time}{ev.endTime ? ` – ${ev.endTime}` : ''}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <i className="ri-map-pin-line text-gray-400 text-xs shrink-0"></i>
                        <span className="truncate">{ev.location}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <i className="ri-user-line text-xs"></i>
                        {ev.ageRecommendation}
                      </div>
                    </div>

                    {ev.memberPerk && (
                      <div className="flex items-center gap-1.5 bg-rose-50 border border-rose-100 rounded-lg px-2 py-1.5 mb-2">
                        <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                          <i className={`${memberPerkConfig[ev.memberPerk].icon} text-rose-500 text-[10px]`}></i>
                        </div>
                        <span className="text-[10px] font-semibold text-rose-600">{memberPerkConfig[ev.memberPerk].label}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-gray-50">
                      <button
                        onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
                        className="text-xs font-semibold text-pink-500 hover:text-pink-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        View Details →
                      </button>
                      <a
                        href={ev.mapLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="w-7 h-7 flex items-center justify-center bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-map-2-line text-gray-400 text-xs"></i>
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Apply to be listed */}
        <div className="mt-10 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">
            Want your event featured here?{' '}
            <button
              onClick={onSubmitEvent}
              className="text-pink-600 font-semibold hover:text-pink-700 transition-colors cursor-pointer"
            >
              Apply to be listed.
            </button>
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <i className="ri-shield-check-line text-gray-300"></i>
            All events are reviewed before publishing
          </div>
        </div>
      </div>
    </section>
  );
}
